import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import MainLayout from '@/components/layout/MainLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, AlertCircle, Loader2, Terminal, LogIn } from 'lucide-react';

// Gateway that mints the CanXP Code device token. Overridable via env.
const GATEWAY_URL =
  (import.meta as any).env?.VITE_GATEWAY_API_URL || 'https://gateway.canxp.ai';

/**
 * CanXP Code device-login approval.
 *
 * CanXP Code opens this page (canxp.ai/link?code=XXXX-XXXX) in whatever browser the machine
 * happens to use, so a signed-out visitor is the normal case, not an edge case — it may not even
 * be the browser the tool was downloaded from. The page therefore renders for everyone and asks
 * for sign-in itself, keeping the code in the URL so it survives the round-trip.
 */
const DeviceLink: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, session, loading } = useAuth();

  const code = (searchParams.get('code') || '').trim();
  const [status, setStatus] = useState<'idle' | 'approving' | 'approved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Kept in the URL, not in router state: an OAuth sign-in is a full page load, and router state
  // does not survive one. Losing the code here strands the terminal waiting forever.
  const selfPath = `/link${code ? `?code=${encodeURIComponent(code)}` : ''}`;
  const signInPath = `/auth?returnUrl=${encodeURIComponent(selfPath)}`;

  const approve = async () => {
    if (!session?.access_token) {
      setError('You must be signed in to approve a device.');
      setStatus('error');
      return;
    }
    if (!code) {
      setError('No device code in the link.');
      setStatus('error');
      return;
    }
    setStatus('approving');
    setError(null);
    try {
      const res = await fetch(`${GATEWAY_URL}/api/device-auth/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as any));
        throw new Error(data?.error?.message || data?.error || `Approval failed (HTTP ${res.status})`);
      }
      setStatus('approved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed.');
      setStatus('error');
    }
  };

  const codeBlock = (
    <div className="rounded-md border bg-muted/40 px-4 py-3 text-center font-mono text-lg tracking-widest">
      {code || '— no code —'}
    </div>
  );

  // Auth is still resolving. Showing the sign-in prompt now would flash it at people who are in
  // fact signed in.
  const body = loading ? (
    <div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Checking your session…
    </div>
  ) : status === 'approved' ? (
    <div className="flex items-start gap-2 text-emerald-600">
      <Check className="h-5 w-5 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-semibold">Approved.</p>
        <p className="text-sm text-muted-foreground">
          Return to CanXP Code — it will finish signing you in automatically.
        </p>
      </div>
    </div>
  ) : status === 'error' ? (
    <div className="flex items-start gap-2 text-destructive">
      <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
      <p>{error}</p>
    </div>
  ) : !user ? (
    <>
      <p className="text-sm text-muted-foreground">
        Sign in to approve this device. Your code is kept and you will come straight back here.
      </p>
      {codeBlock}
    </>
  ) : (
    <>
      <p className="text-sm text-muted-foreground">
        Signed in as <span className="font-medium text-foreground">{user?.email}</span>.
        Confirm this code matches the one shown in your terminal:
      </p>
      {codeBlock}
    </>
  );

  const footer = loading ? null : status === 'approved' ? (
    <Button className="w-full" onClick={() => navigate('/dashboard')}>
      Done
    </Button>
  ) : !user ? (
    <div className="w-full space-y-2">
      <Button className="w-full" asChild>
        <Link to={signInPath} state={{ returnUrl: selfPath }}>
          <LogIn className="mr-2 h-4 w-4" /> Sign in to approve
        </Link>
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        No account?{' '}
        <Link to={signInPath} state={{ returnUrl: selfPath }} className="underline">
          Create one
        </Link>{' '}
        — it only takes a moment.
      </p>
    </div>
  ) : (
    <Button className="w-full" onClick={approve} disabled={!code || status === 'approving'}>
      {status === 'approving' ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Approving…
        </>
      ) : (
        <>
          <Check className="mr-2 h-4 w-4" /> Approve sign-in
        </>
      )}
    </Button>
  );

  return (
    <MainLayout>
      <div className="flex items-center justify-center min-h-[70vh] p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Terminal className="h-5 w-5 text-canada-red" />
              <CardTitle>Sign in to CanXP Code</CardTitle>
            </div>
            <CardDescription>
              {status === 'approved'
                ? 'You can return to the CanXP Code app.'
                : 'Approve this request to finish signing in to the CanXP Code app.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">{body}</CardContent>

          <CardFooter className="flex gap-3">{footer}</CardFooter>
        </Card>
      </div>
    </MainLayout>
  );
};

export default DeviceLink;
